import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtTL, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

export default function QuotePrint() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get(`/quotes/${id}`).then((r) => setQuote(r.data));
    api.get("/settings/company").then((r) => setSettings(r.data));
  }, [id]);

  if (!quote || !settings) return <div className="p-10 text-center text-muted-foreground">Yükleniyor...</div>;

  const c = quote.customer || {};

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white print:p-0">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .a4-page { box-shadow: none !important; margin: 0 !important; }
        }
        .a4-page {
          width: 210mm; min-height: 297mm; padding: 15mm 15mm 20mm;
          margin: 20px auto; background: white; color: #0f172a;
          box-shadow: 0 6px 30px rgba(0,0,0,0.15);
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 10.5pt; line-height: 1.45;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
          display: flex; flex-direction: column;
        }
        .a4-page, .a4-page table, .a4-page td, .a4-page th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .a4-page h1, .a4-page h2, .a4-page h3 { font-family: 'Outfit', sans-serif; }
        .a4-page .num { font-family: 'JetBrains Mono', monospace; font-feature-settings: 'tnum'; }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-slate-900 text-white px-5 py-3 flex items-center justify-between shadow-lg">
        <button onClick={() => window.close()} className="flex items-center gap-2 text-sm hover:opacity-80">
          <ArrowLeft size={16} /> Kapat
        </button>
        <div className="text-sm">Teklif önizleme · <span className="font-mono">{quote.number}</span></div>
        <Button onClick={() => window.print()} data-testid="quote-print-btn"><Printer size={16} className="mr-2" /> Yazdır / PDF Olarak Kaydet</Button>
      </div>

      <div className="a4-page">
        {/* Header */}
        <div className="flex justify-between items-start gap-6 pb-6 border-b-2" style={{ borderColor: "#E05A47" }}>
          <div className="flex-1">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="Logo" style={{ maxHeight: 70, marginBottom: 12 }} />
            ) : (
              <div style={{ width: 60, height: 60, background: "#E05A47", color: "white", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, fontSize: 32, fontWeight: 900, marginBottom: 10 }}>
                {settings.name?.[0] || "D"}
              </div>
            )}
            {!settings.logo_url && (
              <div style={{ fontSize: "16pt", fontWeight: 800, letterSpacing: "-0.5px", marginBottom: 2 }}>{settings.name}</div>
            )}
            {settings.slogan && <div style={{ fontSize: "9pt", color: "#64748b", marginBottom: 8 }}>{settings.slogan}</div>}
            <div style={{ fontSize: "8.5pt", color: "#334155", lineHeight: 1.7 }}>
              {settings.address && <div>{settings.address}</div>}
              <div>
                {settings.phone && <>Tel: {settings.phone}</>}
                {settings.email && <> · {settings.email}</>}
              </div>
              {settings.website && <div>{settings.website}</div>}
              <div>
                {settings.tax_office && <>V.D: {settings.tax_office}</>}
                {settings.tax_no && <> · V.No: {settings.tax_no}</>}
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 200 }}>
            <div style={{ fontSize: "28pt", fontWeight: 900, letterSpacing: "-1px", color: "#E05A47", lineHeight: 1 }}>TEKLİF</div>
            <div style={{ marginTop: 14, fontSize: "9pt" }}>
              <div style={{ marginBottom: 4 }}><span style={{ color: "#64748b" }}>Teklif No:</span> <span className="num" style={{ fontWeight: 700 }}>{quote.number}</span></div>
              <div style={{ marginBottom: 4 }}><span style={{ color: "#64748b" }}>Tarih:</span> <span className="num">{fmtDate(quote.date)}</span></div>
              <div style={{ marginBottom: 4 }}><span style={{ color: "#64748b" }}>Geçerlilik:</span> <span className="num" style={{ fontWeight: 700, color: "#E05A47" }}>{fmtDate(quote.valid_until)}</span></div>
              <div><span style={{ color: "#64748b" }}>Para Birimi:</span> <span className="num" style={{ fontWeight: 700 }}>{quote.currency || "TRY"}</span></div>
            </div>
          </div>
        </div>

        {/* Customer */}
        <div style={{ marginTop: 18, background: "#f1f5f9", borderRadius: 6, padding: "12px 16px", borderLeft: "3px solid #E05A47" }}>
          <div style={{ fontSize: "8pt", textTransform: "uppercase", letterSpacing: "1.5px", color: "#64748b", fontWeight: 700, marginBottom: 6 }}>Müşteri</div>
          <div style={{ fontSize: "12pt", fontWeight: 800, marginBottom: 4 }}>{c.name}</div>
          <div style={{ fontSize: "9pt", color: "#334155", lineHeight: 1.6 }}>
            {c.contact && <div>Yetkili: {c.contact}</div>}
            <div>
              {c.phone && <>Tel: {c.phone}</>}
              {c.email && <> · {c.email}</>}
            </div>
            {c.address && <div>{c.address}</div>}
            {c.tax_no && <div>V.No: {c.tax_no}</div>}
          </div>
        </div>

        {/* Items table */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 20, fontSize: "9.5pt", color: "#0f172a" }}>
          <thead>
            <tr style={{ background: "#0B0F17", color: "white" }}>
              <th style={{ padding: "8px 6px", textAlign: "center", width: 30 }}>#</th>
              <th style={{ padding: "8px 6px", textAlign: "left" }}>Ürün / Açıklama</th>
              <th style={{ padding: "8px 6px", textAlign: "right", width: 70 }}>Miktar</th>
              <th style={{ padding: "8px 6px", textAlign: "left", width: 60 }}>Birim</th>
              <th style={{ padding: "8px 6px", textAlign: "right", width: 90 }}>Birim Fiyat</th>
              <th style={{ padding: "8px 6px", textAlign: "right", width: 55 }}>KDV</th>
              <th style={{ padding: "8px 6px", textAlign: "right", width: 100 }}>Toplam</th>
            </tr>
          </thead>
          <tbody>
            {quote.items.map((it, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #94a3b8" }}>
                <td style={{ padding: "8px 6px", textAlign: "center", color: "#1e293b", fontWeight: 600 }} className="num">{String(i + 1).padStart(2, "0")}</td>
                <td style={{ padding: "8px 6px", color: "#0f172a" }}>
                  <div style={{ fontWeight: 700 }}>{it.product_name}</div>
                  {it.description && <div style={{ fontSize: "8pt", color: "#334155" }}>{it.description}</div>}
                </td>
                <td style={{ padding: "8px 6px", textAlign: "right", color: "#0f172a", fontWeight: 600 }} className="num">{it.quantity}</td>
                <td style={{ padding: "8px 6px", textTransform: "uppercase", fontSize: "8.5pt", color: "#0f172a", fontWeight: 600 }}>{it.unit}</td>
                <td style={{ padding: "8px 6px", textAlign: "right", color: "#0f172a", fontWeight: 600 }} className="num">{fmtTL(it.unit_price)}</td>
                <td style={{ padding: "8px 6px", textAlign: "right", color: "#0f172a", fontWeight: 600 }} className="num">%{it.vat_rate}</td>
                <td style={{ padding: "8px 6px", textAlign: "right", fontWeight: 800, color: "#0f172a" }} className="num">{fmtTL(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <div style={{ minWidth: 300, fontSize: "10pt" }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
              <span style={{ color: "#334155", fontWeight: 600 }}>Ara Toplam</span><span className="num" style={{ color: "#0f172a", fontWeight: 600 }}>{fmtTL(quote.subtotal)}</span>
            </div>
            {quote.discount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", color: "#b91c1c" }}>
                <span style={{ fontWeight: 600 }}>İskonto</span><span className="num" style={{ fontWeight: 600 }}>-{fmtTL(quote.discount)}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
              <span style={{ color: "#334155", fontWeight: 600 }}>KDV</span><span className="num" style={{ color: "#0f172a", fontWeight: 600 }}>{fmtTL(quote.vat_amount)}</span>
            </div>
            {quote.tevkifat_enabled && quote.tevkifat_amount > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", color: "#d97706" }}>
                  <span>KDV Tevkifatı ({quote.tevkifat_rate})</span><span className="num">-{fmtTL(quote.tevkifat_amount)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", fontWeight: 600, borderTop: "1px dashed #cbd5e1", marginTop: 2 }}>
                  <span>Ödenecek KDV</span><span className="num">{fmtTL(quote.vat_amount - quote.tevkifat_amount)}</span>
                </div>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 12px", marginTop: 6, background: "#0B0F17", color: "white", borderRadius: 4, fontWeight: 800, fontSize: "12pt" }}>
              <span>GENEL TOPLAM</span><span className="num">{fmtTL(quote.total)}</span>
            </div>
          </div>
        </div>

        {/* Terms */}
        {(quote.payment_terms || quote.delivery_terms || quote.description) && (
          <div style={{ marginTop: 30, fontSize: "9pt", lineHeight: 1.65 }}>
            {quote.payment_terms && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: "8pt", textTransform: "uppercase", letterSpacing: "1px", color: "#64748b", fontWeight: 700, marginBottom: 3 }}>Ödeme Şartları</div>
                <div style={{ color: "#334155" }}>{quote.payment_terms}</div>
              </div>
            )}
            {quote.delivery_terms && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: "8pt", textTransform: "uppercase", letterSpacing: "1px", color: "#64748b", fontWeight: 700, marginBottom: 3 }}>Teslimat Şartları</div>
                <div style={{ color: "#334155" }}>{quote.delivery_terms}</div>
              </div>
            )}
            {quote.description && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: "8pt", textTransform: "uppercase", letterSpacing: "1px", color: "#64748b", fontWeight: 700, marginBottom: 3 }}>Açıklama</div>
                <div style={{ color: "#334155" }}>{quote.description}</div>
              </div>
            )}
          </div>
        )}

        {/* Signatures */}
        <div style={{ marginTop: 48, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
          <div>
            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "9pt" }}>Teklifi Hazırlayan</div>
            <div style={{ color: "#334155", fontSize: "8.5pt", marginTop: 2 }}>{settings.name}</div>
            <div style={{ height: 80 }} />
            <div style={{ borderTop: "1px solid #0f172a", paddingTop: 6, fontSize: "8.5pt", color: "#334155" }}>Ad Soyad / İmza / Kaşe</div>
          </div>
          <div>
            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "9pt" }}>Müşteri Onayı</div>
            <div style={{ color: "#334155", fontSize: "8.5pt", marginTop: 2 }}>{c.name}</div>
            <div style={{ height: 80 }} />
            <div style={{ borderTop: "1px solid #0f172a", paddingTop: 6, fontSize: "8.5pt", color: "#334155" }}>Ad Soyad / İmza / Kaşe</div>
          </div>
        </div>

        {/* Footer - pinned to bottom of the page */}
        <div style={{ marginTop: "auto", paddingTop: 10, borderTop: "1px solid #e2e8f0", fontSize: "7.5pt", color: "#94a3b8", textAlign: "center" }}>
          {settings.footer_text && <div>{settings.footer_text}</div>}
          <div style={{ marginTop: 4 }}>
            {settings.name}
            {settings.address && ` · ${settings.address}`}
            {settings.phone && ` · ${settings.phone}`}
            {settings.website && ` · ${settings.website}`}
          </div>
        </div>
      </div>
    </div>
  );
}
